import { useState } from "react";
import { DocsComponentDemo } from "../DocsComponentDemo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MapPin, Briefcase, Clock, ArrowRight } from "lucide-react";

// Mock brand data
const mockBrand = {
  name: "TechFlow Inc.",
  logoUrl: null, // Using text fallback
  color: "#6366f1", // Indigo brand color
};

// Mock jobs data
const mockJobs = [
  {
    id: "job-1",
    title: "Senior Frontend Engineer",
    location: "San Francisco, CA",
    locationType: "hybrid",
    employmentType: "fulltime",
  },
  {
    id: "job-2",
    title: "Product Designer",
    location: "Remote",
    locationType: "remote",
    employmentType: "fulltime",
  },
  {
    id: "job-3",
    title: "DevOps Engineer",
    location: "New York, NY",
    locationType: "onsite",
    employmentType: "contract",
  },
];

const getEmploymentLabel = (type: string) => {
  const labels: Record<string, string> = {
    fulltime: "Full-time",
    parttime: "Part-time",
    contract: "Contract",
    internship: "Internship",
  };
  return labels[type] || type;
};

const getLocationTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    remote: "Remote",
    hybrid: "Hybrid",
    onsite: "On-site",
  };
  return labels[type] || type;
};

export function DemoCareerPage() {
  const [hoveredJob, setHoveredJob] = useState<string | null>(null);
  const template = "modern";
  const brandColor = mockBrand.color;

  return (
    <DocsComponentDemo 
      caption="Mini career page preview showing company branding and job listings"
      fullWidth
    >
      <div
        className={cn(
          "rounded-lg overflow-hidden",
          template === "modern" && "bg-gradient-to-br from-card to-muted/50"
        )}
      >
        {/* Header */}
        <header
          className="px-4 py-6"
          style={{ background: `linear-gradient(135deg, ${brandColor}15, ${brandColor}05)` }}
        >
          <div className="max-w-xl mx-auto">
            <div
              className="mb-4 text-xl font-bold"
              style={{ color: brandColor }}
            >
              {mockBrand.name}
            </div>

            <div className="rounded-xl bg-card/80 p-4 backdrop-blur">
              <p
                className="mb-2 text-xl font-bold"
                style={{ color: brandColor }}
              >
                Join Our Team
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We're looking for passionate people to join us on our mission. Check out our open positions below.
              </p>
              <a
                href="#positions"
                className="mt-4 inline-block px-4 py-2 text-sm font-medium text-white rounded-full transition-opacity hover:opacity-90"
                style={{ backgroundColor: brandColor }}
                onClick={(e) => e.preventDefault()}
              >
                View Open Positions
              </a>
            </div>
          </div>
        </header>

        {/* Jobs List */}
        <section id="positions" className="px-4 py-6">
          <div className="max-w-xl mx-auto">
            <h2 className="mb-4 text-lg font-semibold text-heading">
              Open Positions ({mockJobs.length})
            </h2>
            
            <div className="space-y-3">
              {mockJobs.map((job) => (
                <div
                  key={job.id}
                  className={cn(
                    "group w-full border p-4 text-left transition-all cursor-default",
                    "rounded-xl bg-card/80 backdrop-blur",
                    hoveredJob === job.id && "shadow-md"
                  )}
                  style={{
                    borderColor: hoveredJob === job.id ? `${brandColor}50` : undefined,
                  }}
                  onMouseEnter={() => setHoveredJob(job.id)}
                  onMouseLeave={() => setHoveredJob(null)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-semibold text-heading transition-colors text-sm"
                        style={{ color: hoveredJob === job.id ? brandColor : undefined }}
                      >
                        {job.title}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          {getLocationTypeLabel(job.locationType)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getEmploymentLabel(job.employmentType)}
                        </span>
                      </div>
                    </div>
                    <ArrowRight 
                      className={cn(
                        "h-4 w-4 transition-all shrink-0 mt-1",
                        hoveredJob === job.id 
                          ? "translate-x-0 opacity-100" 
                          : "-translate-x-1 opacity-0"
                      )}
                      style={{ color: brandColor }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border bg-card/50 px-4 py-4">
          <div className="max-w-xl mx-auto flex items-center justify-between">
            <span
              className="text-sm font-medium"
              style={{ color: brandColor }}
            >
              {mockBrand.name}
            </span>
            <span className="text-xs text-muted-foreground">
              Powered by Beatview
            </span>
          </div>
        </footer>
      </div>
    </DocsComponentDemo>
  );
}
